import {fetchCAPI} from './fetch-capi'
import type {SessionEvent} from '../types/events'

/**
 * Internal query data shape stored in React Query cache.
 * Tracks accumulated events and the cursor for incremental loading.
 */
export interface EventsQueryData {
  events: SessionEvent[]
  /** The cursor (last event ID) for fetching newer events, or null if no events yet */
  afterCursor: string | null
  /** Total event count reported by the API */
  total: number
}

/** Abstraction over the network call for testability. */
export type EventsFetcher = (path: string) => Promise<Response>

/** React Query key for a task's accumulated session events. */
export function generateEventsQueryKey(taskId: string | undefined): string[] {
  return ['agent-session-events', taskId ?? '']
}

const defaultFetcher: EventsFetcher = (path: string) =>
  fetchCAPI({path, additionalHeaders: {Accept: 'application/json'}})

/**
 * Parse a Link header and extract the `after` cursor value from the rel="next" URL.
 * We extract just the cursor value (not the full URL) because the server-side path
 * differs from the client-side CAPI path.
 */
export function parseAfterCursor(linkHeader: string | null): string | null {
  if (!linkHeader) return null
  for (const part of linkHeader.split(',')) {
    const match = part.match(/<([^>]+)>;\s*rel="([^"]+)"/)
    if (match && match[1] && match[2] === 'next') {
      try {
        const url = new URL(match[1], 'http://placeholder')
        return url.searchParams.get('after')
      } catch {
        continue
      }
    }
  }
  return null
}

/**
 * Fetch events from the API, optionally using an after cursor for incremental loading.
 * Without a cursor, returns all events. With a cursor, returns only events after it.
 */
export async function fetchEvents(
  taskId: string,
  afterCursor?: string | null,
  fetcher: EventsFetcher = defaultFetcher,
): Promise<{events: SessionEvent[]; total: number; afterCursor: string | null}> {
  let path = `/agents/tasks/${taskId}/events`
  if (afterCursor) {
    path += `?after=${encodeURIComponent(afterCursor)}`
  }

  const response = await fetcher(path)
  const body = (await response.json()) as {events: SessionEvent[]; total: number}
  const linkHeader = response.headers.get('Link')
  const nextCursor = parseAfterCursor(linkHeader)

  return {
    events: body.events,
    total: body.total,
    afterCursor: nextCursor,
  }
}

/**
 * Fetch all events for a task with cursor-based incremental loading.
 * On first call (no previousData), fetches all events.
 * On subsequent calls, uses the afterCursor to fetch incremental events and reconciles
 * them by ID. Synthetic events do not advance the cursor and may be returned again
 * with updated pending or dismissed state.
 */
export async function fetchAllEvents(
  taskId: string,
  previousData?: EventsQueryData,
  fetcher: EventsFetcher = defaultFetcher,
): Promise<EventsQueryData> {
  if (!previousData || !previousData.afterCursor) {
    // Full load — no cursor, fetch everything
    const result = await fetchEvents(taskId, null, fetcher)
    return {
      events: result.events,
      afterCursor: result.afterCursor,
      total: result.total,
    }
  }

  // Incremental load — fetch only events after the cursor
  const result = await fetchEvents(taskId, previousData.afterCursor, fetcher)
  const afterCursor = result.afterCursor ?? previousData.afterCursor

  if (result.events.length === 0) {
    // No new events — keep previous data, update total
    if (result.total === previousData.total && afterCursor === previousData.afterCursor) {
      return previousData
    }

    return {
      events: previousData.events,
      afterCursor,
      total: result.total,
    }
  }

  const events: SessionEvent[] = []
  const eventIndexById = new Map<string, number>()

  for (const event of [...previousData.events, ...result.events]) {
    const existingIndex = eventIndexById.get(event.id)
    if (existingIndex === undefined) {
      eventIndexById.set(event.id, events.length)
      events.push(event)
    } else {
      events[existingIndex] = event
    }
  }

  return {
    events,
    afterCursor,
    total: result.total,
  }
}
